/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `ConnectionRequestBanner` for the Caintra Connect frontend.
 * @module components/Chat/ConnectionRequestBanner
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { logger, sanitizeError } from "../../utils/logger.js";
import {
  handleViewProfile,
  handleRejectBusinessEventConnectionRequest,
  handleAcceptBusinessEventConnectionRequest,
  handleAcceptRequest,
  handleRejectRequest,
} from "../../logic/chat";

const SUPPORTED_REQUEST_TYPES = new Set([
  "businessEventConnectionRequest",
  "customerCompanyUserConnectionRequest",
]);

const ConnectionRequestBanner = ({
  readOnlyRequestType,
  readOnlyRequest,
  isAcceptButtonDisabled,
  setIsAcceptButtonDisabled,
  onConnectionRequestsUpdate,
  onClearReadOnlyChat,
  setChats,
  setChatParticipants,
  styles,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleReject = async () => {
    if (!readOnlyRequest) {
      return;
    }

    if (readOnlyRequestType === "businessEventConnectionRequest") {
      await handleRejectBusinessEventConnectionRequest(
        readOnlyRequest,
        navigate,
        onConnectionRequestsUpdate,
      );
      if (onClearReadOnlyChat) {
        onClearReadOnlyChat();
      }
    } else if (readOnlyRequestType === "customerCompanyUserConnectionRequest") {
      await handleRejectRequest(
        readOnlyRequest._id,
        [readOnlyRequest],
        navigate,
        onConnectionRequestsUpdate,
      );
      if (onClearReadOnlyChat) {
        onClearReadOnlyChat();
      }
    } else {
      // Unsupported readOnlyRequestType for reject action.
    }
  };

  const handleAccept = async () => {
    if (!readOnlyRequest || isAcceptButtonDisabled) {
      return;
    }

    if (readOnlyRequestType === "businessEventConnectionRequest") {
      setIsAcceptButtonDisabled(true);
      try {
        await handleAcceptBusinessEventConnectionRequest(
          readOnlyRequest,
          navigate,
          t,
          setIsAcceptButtonDisabled,
          onConnectionRequestsUpdate,
        );
        if (onClearReadOnlyChat) {
          onClearReadOnlyChat();
        }
      } catch (error) {
        logger.error("Error accepting business event connection request:", {
          err: sanitizeError(error),
        });
        setIsAcceptButtonDisabled(false);
      }
    } else if (readOnlyRequestType === "customerCompanyUserConnectionRequest") {
      setIsAcceptButtonDisabled(true);
      try {
        await handleAcceptRequest(
          readOnlyRequest._id,
          [readOnlyRequest],
          navigate,
          onConnectionRequestsUpdate,
          setChats,
          setChatParticipants,
        );
        if (onClearReadOnlyChat) {
          onClearReadOnlyChat();
        }
      } catch (error) {
        logger.error("Error accepting connection request:", {
          err: sanitizeError(error),
        });
        setIsAcceptButtonDisabled(false);
      }
    } else {
      // Unsupported readOnlyRequestType for accept action.
    }
  };

  if (!SUPPORTED_REQUEST_TYPES.has(readOnlyRequestType)) {
    return null;
  }

  return (
    <div className={styles.requestBanner}>
      <div className={styles.requestBannerTitle}>
        {readOnlyRequestType === "businessEventConnectionRequest"
          ? t("pages.chat.connectionRequest.businessEventTitle")
          : t("pages.chat.connectionRequest.buyerTitle")}
      </div>
      <div className={styles.requestBannerSubtitle}>
        {t("pages.chat.connectionRequest.subtitle")}
      </div>
      <div className={styles.requestBannerActions}>
        <button
          className={`${styles.requestBannerButton} ${styles.requestBannerReject}`}
          onClick={handleReject}
        >
          {t("pages.chat.connectionRequest.reject")}
        </button>
        <button
          className={`${styles.requestBannerButton} ${styles.requestBannerView}`}
          onClick={() => {
            if (readOnlyRequest) {
              handleViewProfile(readOnlyRequest, navigate);
            }
          }}
        >
          {t("pages.chat.connectionRequest.viewProfile")}
        </button>
        <button
          className={`${styles.requestBannerButton} ${styles.requestBannerAccept}`}
          disabled={isAcceptButtonDisabled}
          onClick={handleAccept}
        >
          {t("pages.chat.connectionRequest.accept")}
        </button>
      </div>
    </div>
  );
};

export default ConnectionRequestBanner;
